import "react-simple-keyboard/build/css/index.css";

import Keyboard, { SimpleKeyboard } from "react-simple-keyboard";

import React, { useImperativeHandle, useLayoutEffect, useRef } from "react";

import { LAYOUT, LAYOUT_DISPLAY, LayoutType } from "./const";
import { isVirtualKeyboardEnabled } from "../../config/const";
import { useDigitalReceptionMetadata } from "../../lib/hooks/useQueryBoard";

export { type LayoutType };

export interface VirtualKeyboardProps {
  layoutName?: LayoutType;
  inputName?: string;
  disableButtonHold?: boolean;
  onChange?: (input: string, e?: MouseEvent) => void;
  onKeyPress?: (key: string) => void;
}

export interface VirtualKeyboardRef {
  setInput: (
    input: string,
    inputName?: string | undefined,
    skipSync?: boolean | undefined,
  ) => void;
  setCaretPosition: (
    position: number | null,
    endPosition?: number | null | undefined,
  ) => void;
}

const VirtualKeyboard = React.forwardRef<
  VirtualKeyboardRef,
  VirtualKeyboardProps
>((props, ref) => {
  const { layoutName = "default" } = props;

  const keyboardRef = useRef<SimpleKeyboard>();
  const rootRef = useRef<HTMLDivElement>(null);
  const metadata = useDigitalReceptionMetadata();

  useImperativeHandle(ref, () => ({
    setInput: (
      input: string,
      inputName?: string | undefined,
      skipSync?: boolean | undefined,
    ) => {
      if (!keyboardRef.current) {
        return;
      }

      keyboardRef.current.setInput(input, inputName, skipSync);
    },
    setCaretPosition: (
      position: number | null,
      endPosition?: number | null | undefined,
    ) => {
      if (!keyboardRef.current) {
        return;
      }

      keyboardRef.current.setCaretPosition(position, endPosition);
    },
  }));

  const offset = metadata?.keyboardBottomOffset ?? 0;

  useLayoutEffect(() => {
    const el = rootRef.current?.closest(".keyboard");

    if (!(el instanceof HTMLElement)) {
      return undefined;
    }

    el.style.setProperty("--keyboard-offset", `${offset}rem`);

    return () => {
      el.style.removeProperty("--keyboard-offset");
    };
  }, [offset]);

  // Disable virtual keyboard if we are inside a webview
  // or if it is set that way in the metadata
  if (!isVirtualKeyboardEnabled || metadata?.isVirtualKeyboardDisabled) {
    return null;
  }

  return (
    <div ref={rootRef}>
      <Keyboard
        {...props}
        keyboardRef={(ref) => {
          keyboardRef.current = ref;
        }}
        tabCharOnTab={false}
        mergeDisplay
        layout={LAYOUT}
        display={LAYOUT_DISPLAY[layoutName]}
        preventMouseDownDefault
      />
    </div>
  );
});

export default VirtualKeyboard;
